<template>
  <client-only>
    <vz-form
      v-model="isFormValid"
      ref="vzForm"
      class="vz-commercial-offer-form text-body position-relative"
    >
      <vz-loader :status-loader="loader" />
      <div class="vz-commercial-offer-form-section flex flex-space-between">
        <div class="vz-commercial-offer-form-block">
          <div class="vz-commercial-offer-form-part">
            <div class="vz-commercial-offer-form-field">
              <vz-input
                large
                required
                label-public-style
                v-model="form.name"
                label="Ваше имя"
                placeholder="Укажите ваше имя"
                :rules="[rules.required]"
              />
            </div>
            <div class="vz-commercial-offer-form-field">
              <vz-phone-input
                large
                required
                label-public-style
                v-model="form.phone"
                label="Ваш телефон"
                :rules="[rules.required, rules.phone]"
              />
            </div>
            <div class="vz-commercial-offer-form-field">
              <vz-input
                large
                required
                label-public-style
                v-model="form.email"
                label="Email"
                placeholder="Ваша почта"
                :rules="[rules.required, rules.email]"
              />
            </div>
          </div>
          <div class="vz-commercial-offer-form-part">
            <div class="vz-commercial-offer-form-field">
              <vz-input
                large
                required
                label-public-style
                v-model="form.scope"
                label="Сфера деятельности"
                placeholder="Ваша сфера деятельности"
                :rules="[rules.required]"
              />
            </div>
            <div class="vz-commercial-offer-form-field">
              <vz-input
                large
                required
                label-public-style
                v-model="form.company"
                label="Компания"
                placeholder="Название компании"
                :rules="[rules.required]"
              />
            </div>
            <div class="vz-commercial-offer-form-field">
            <city-autocomplete
              large
              required
              label-public-style
              v-model="form.city"
              label="Ваш город"
              :rules="[rules.required]"
            />
            </div>
          </div>
        </div>
        <div class="vz-commercial-offer-form-block">
          <div class="vz-commercial-offer-form-field vz-commercial-offer-form-comment">
            <vz-textarea
              label-public-style
              v-model="form.comment"
              label="Комментарий"
              placeholder="Ваш комментарий"
              rows="11"
              max-length="800"
            />
          </div>
        </div>
      </div>
      <div class="vz-commercial-offer-form-section vz-commercial-offer-form-footer flex flex-space-between">
        <div class="vz-commercial-offer-form-block vz-commercial-offer-form-block-double">
          <vz-privacy-politics :label="btnTitle" />
        </div>
        <div class="vz-commercial-offer-form-block">
          <div class="vz-commercial-offer-form-field">
            <vz-button
              large
              variant="primary"
              :disabled="!isFormValid"
              class="mt-20"
              @click="sendForm"
            >
              {{ btnTitle }}
            </vz-button>
          </div>
        </div>
      </div>
    </vz-form>
  </client-only>
</template>

<script setup lang="ts">
import formRules from '~/common/vz-form-rules'
import VzPhoneInput from '~/components/ui-kit/vz-phone-input.vue'
import CityAutocomplete from '~/components/autocomplete/city-autocomplete.vue'
import VzTextarea from '~/components/ui-kit/vz-textarea.vue'
import VzForm from '~/components/ui-kit/vz-form.vue'
import VzPrivacyPolitics from '~/components/public/vz-privacy-politics.vue'
import { sendOfferRequest, sendOffer } from '~/api/city'
import { CommercialOfferForm } from '~~/types/forms'

const props = defineProps({
  feedback: {
    type: Boolean,
    default: true
  }
})

const { $showToast } = useNuxtApp()

const vzForm = ref<typeof VzForm|null>(null)
const loader = ref(false)
const isFormValid = ref(true)
const rules = ref({ ...formRules })

const form = ref({
  scope: '',
  city: '',
  company: '',
  name: '',
  email: '',
  phone: '',
  comment: ''
} as CommercialOfferForm)

const btnTitle = 'Получить информацию'

async function sendForm(): Promise<void> {
  await vzForm.value?.validate()
  if (!isFormValid.value) {
    return
  }

  loader.value = true
  const res = props.feedback
    ? await sendOffer(form.value)
    : await sendOfferRequest(form.value)

  if (res.message) {
    $showToast({
      variant: 'success',
      title: 'Коммерческое предложение',
      text: res.message,
    })
    cleanForm()
  }
  loader.value = false
}

function cleanForm(): void {
  form.value = {
    scope: '',
    city: '',
    company: '',
    name: '',
    email: '',
    phone: '',
    comment: ''
  }
}
</script>

<style lang="scss">
.vz-commercial-offer-form {

  &-section {
    @media only screen and (max-width: $pub-sm) {
      flex-wrap: wrap;
    }
  }

  &-block {
    max-width: 700px;
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 40px;

    @media only screen and (max-width: $pub-md) {
      max-width: 590px;
    }   

    @media only screen and (max-width: $pub-xs) {
      max-width: none;
      grid-template-columns: 1fr;
      grid-gap: 0;
    } 

    &:last-child {
      max-width: 330px;
      margin-left: 40px;

      @media only screen and (max-width: $pub-md) {
        max-width: 280px;
      } 

      @media only screen and (max-width: $pub-sm) {
        margin-left: 0;
        max-width: none;
        width: 100%;
        grid-template-columns: 1fr;
      } 

      .vz-commercial-offer-form-field {
        @media only screen and (max-width: $pub-sm) {
          width: 100%;
          max-width: none;
        } 
      }
    }

    &-double {
      grid-template-columns: 1fr;
    }
  }

  &-field {
    width: 330px;
    margin-bottom: 30px;

    @media only screen and (max-width: $pub-md) {
      max-width: 280px;
    }

    @media only screen and (max-width: $pub-xs) {
      max-width: none;
      width: 100%;
    } 

    span {
      margin-bottom: 5px;
      display: block;
    }
  }

  &-footer {
    @media only screen and (max-width: $pub-sm) {
      flex-wrap: nowrap;
    } 

    @media only screen and (max-width: $pub-xs) {
      flex-wrap: wrap;
    } 
  }

  .vz-privacy-politics {
    max-width: none;
    text-align: left;

    a {
      color: $primary;
      text-decoration: underline;
    }
  }

  textarea {
    font-size: $fs-16;
    line-height: $fs-20;

    @media screen and (max-width: $pub-sm) {      
      width: 100%;
    }
  }

  .vz-button {
    max-width: 330px;
    width: 100%;

    @media only screen and (max-width: $pub-xs) {
      margin-left: auto;
      margin-right: auto;
    } 

    &-title {
      margin-left: auto;
      margin-right: auto;
    }
  }
}
</style>
